import { useEffect } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import Collection from '@model-core/core/Collection';
import AppRoleModel from '@/models/references/AppRoleModel';
import CompanyRoleModel from '@/models/references/CompanyRoleModel';
import ExportStatusModel from '@/models/references/ExportStatusModel';
import ExportTypeModel from '@/models/references/ExportTypeModel';
import PeriodStatusModel from '@/models/references/PeriodStatusModel';
import TeamRoleModel from '@/models/references/TeamRoleModel';
import TeamMemberStatusModel from '@/models/references/TeamMemberStatusModel';
import TeamStatusModel from '@/models/references/TeamStatusModel';
import TimeSheetStatusModel from '@/models/references/TimeSheetStatusModel';
import TimeSheetTypeModel from '@/models/references/TimeSheetTypeModel';
import TimeSheetSignatureTypeModel from '@/models/references/TimeSheetSignatureTypeModel';
import UserStatusModel from '@/models/references/UserStatusModel';

export interface ReferenceOption {
  id: number;
  /** Clé utilisée par le front (nom du cas de l'enum : EMPLOYEE…) */
  reference: string;
  label: string;
  /** URN à envoyer à l'API pour écrire la relation (un filtre prend l'id) */
  urn: string;
  /** Ordre d'affichage donné par l'API (ex. CompanyRole : Dirigeant, Assistant, Employé), sinon tri par libellé */
  position?: number;
}

/** Par position quand l'API en donne une, sinon par libellé (l'API renvoie les enums dans leur ordre de déclaration) */
export function compareReferenceOptions(a: ReferenceOption, b: ReferenceOption): number {
  if (a.position !== undefined && b.position !== undefined && a.position !== b.position) {
    return a.position - b.position;
  }
  return a.label.localeCompare(b.label, 'fr');
}

/** Listes de référence de l'API (enums exposés en ressources) : valeurs et libellés viennent de l'API, pas du front */
const REFERENCES = {
  companyRoles: CompanyRoleModel,
  appRoles: AppRoleModel,
  userStatuses: UserStatusModel,
  teamRoles: TeamRoleModel,
  teamStatuses: TeamStatusModel,
  teamMemberStatuses: TeamMemberStatusModel,
  periodStatuses: PeriodStatusModel,
  timeSheetStatuses: TimeSheetStatusModel,
  timeSheetTypes: TimeSheetTypeModel,
  timeSheetSignatureTypes: TimeSheetSignatureTypeModel,
  exportTypes: ExportTypeModel,
  exportStatuses: ExportStatusModel,
} as const;

export type ReferenceName = keyof typeof REFERENCES;

interface ReferenceStore {
  lists: Partial<Record<ReferenceName, ReferenceOption[]>>;
  load: (name: ReferenceName) => Promise<void>;
}

/** Chargements en cours ou terminés : une requête par liste et par session */
const loads = new Map<ReferenceName, Promise<void>>();

export const useReferenceStore = create<ReferenceStore>(() => ({
  lists: {},

  load: (name) => {
    if (!loads.has(name)) {
      const load = new Collection(REFERENCES[name])
        .list()
        .then(({ collection }) => {
          const options = collection.models
            .map((model) => {
              const option: ReferenceOption = {
                id: Number(model.id),
                reference: model.reference,
                label: model.label,
                urn: model.urn,
              };
              // Seuls certains enums ont une position (CompanyRoleModel, TimeSheetStatusModel)
              const { position } = model as { position?: number };
              return position === undefined ? option : { ...option, position };
            })
            .sort(compareReferenceOptions);
          useReferenceStore.setState((state) => ({ lists: { ...state.lists, [name]: options } }));
        })
        .catch(() => {
          // Échec : nouvel essai au prochain affichage
          loads.delete(name);
        });
      loads.set(name, load);
    }
    return loads.get(name)!;
  },
}));

/** Options d'une liste de référence, chargées au besoin (vide pendant le chargement) */
export function useReferenceOptions(name: ReferenceName): ReferenceOption[] {
  const options = useReferenceStore((state) => state.lists[name]);
  const load = useReferenceStore((state) => state.load);

  useEffect(() => {
    void load(name);
  }, [name, load]);

  return options ?? EMPTY;
}

const EMPTY: ReferenceOption[] = [];

/** Option d'une référence (ex. pour envoyer son URN à l'API), la liste étant chargée au besoin */
export async function resolveReference(name: ReferenceName, reference: string): Promise<ReferenceOption> {
  await useReferenceStore.getState().load(name);
  const option = useReferenceStore.getState().lists[name]?.find((item) => item.reference === reference);
  if (!option) throw new Error(`Valeur inconnue : ${reference}`);
  return option;
}

/** Id API d'une référence déjà chargée (filtres de liste), null sinon */
export function findReferenceId(options: ReferenceOption[], reference: string | null | undefined): number | null {
  return options.find((option) => option.reference === reference)?.id ?? null;
}

/** Id d'un enum imbriqué, tel que l'API le renvoie : son identité ({@id, id}) ou une IRI ; null sans valeur */
function enumIdOf(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value.split('/').pop()) || null;
  const identity = value as { id?: unknown; '@id'?: unknown };
  if (identity.id != null) return Number(identity.id);
  return typeof identity['@id'] === 'string' ? Number(identity['@id'].split('/').pop()) || null : null;
}

/** Ids introuvables déjà relancés, par liste (« timeSheetStatuses:7 ») : une seule relance par id */
const misses = new Set<string>();

/** Recharge une liste déjà chargée (la précédente reste affichée jusqu'à la réponse) */
function reloadReferences(name: ReferenceName): Promise<void> {
  loads.delete(name);
  return useReferenceStore.getState().load(name);
}

/**
 * Option d'un enum imbriqué (ex. timeSheetStatus d'une feuille), d'après son id seul : l'API ne renvoie d'un objet
 * imbriqué que son identité ; référence, libellé et position viennent de la liste de référence, que l'écran charge
 * (useReferences, loadReferences). null si la liste n'est pas (encore) chargée ou si la valeur est inconnue.
 * Liste chargée sans cet id (valeur ajoutée côté API pendant la session, ex. déploiement) : la liste est rechargée en
 * arrière-plan, une seule fois par id (un id toujours introuvable ne relance plus : pas de boucle) ; les écrans abonnés
 * (useReferences, useReferenceOptions) se réaffichent avec la nouvelle liste.
 */
export function referenceOf(name: ReferenceName, nested: unknown): ReferenceOption | null {
  const id = enumIdOf(nested);
  if (id === null) return null;
  const list = useReferenceStore.getState().lists[name];
  const option = list?.find((item) => item.id === id) ?? null;
  if (list && !option && !misses.has(`${name}:${id}`)) {
    misses.add(`${name}:${id}`);
    void reloadReferences(name);
  }
  return option;
}

/** Charge ces listes de référence (une requête par liste et par session) : à attendre avant de lire des enums imbriqués */
export async function loadReferences(...names: ReferenceName[]): Promise<void> {
  await Promise.all(names.map((name) => useReferenceStore.getState().load(name)));
}

/**
 * Listes de référence d'un écran, comme le backoffice jts charge les statuts qu'il affiche : les charge au besoin et
 * réaffiche l'écran quand elles arrivent ou sont rechargées (id inconnu : referenceOf). true quand toutes sont là.
 */
export function useReferences(...names: ReferenceName[]): boolean {
  const key = names.join(',');
  // Abonné aux listes elles-mêmes (pas seulement à leur présence) : une liste rechargée réaffiche l'écran
  const lists = useReferenceStore(useShallow((state) => names.map((name) => state.lists[name])));
  const ready = lists.every((list) => list !== undefined);

  useEffect(() => {
    void loadReferences(...(key.split(',') as ReferenceName[]));
  }, [key]);

  return ready;
}
